import React, { useState, useEffect } from 'react';
import {
  Award, Download, Trash2, Search, Filter, RefreshCw,
  ChevronLeft, ChevronRight, Eye, TrendingUp, Users,
  Trophy, FileDown, BarChart3, Loader
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeArenaCertificatesAdmin = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);

  const adminToken = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchCertificates();
    fetchAnalytics();
    fetchContests();
  }, [page, filterType, selectedContest]);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/codearena/certificates?page=${page}&limit=20`;
      if (selectedContest) url += `&contest_id=${selectedContest}`;
      if (filterType) url += `&certificate_type=${filterType}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      setCertificates(res.data.certificates || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch certificates:', err);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/codearena/certificate-analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const fetchContests = async () => {
    try {
      const res = await axios.get(`${API}/admin/codearena/contests`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setContests(res.data.contests || []);
    } catch (err) {
      console.error('Failed to fetch contests:', err);
    }
  };

  const deleteCertificate = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    
    try {
      await axios.delete(`${API}/admin/codearena/certificates/${certId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Certificate deleted');
      fetchCertificates();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to delete certificate');
    }
  };

  const bulkGenerateCertificates = async () => {
    if (!selectedContest) {
      toast.error('Please select a contest first');
      return;
    }
    
    setBulkGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/codearena/certificates/bulk-generate?contest_id=${selectedContest}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Generated ${res.data.generated} certificates`);
      fetchCertificates();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to generate certificates');
    } finally {
      setBulkGenerating(false);
    }
  };

  const filteredCerts = certificates.filter(cert => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      cert.student_name?.toLowerCase().includes(search) ||
      cert.student_email?.toLowerCase().includes(search) ||
      cert.contest_title?.toLowerCase().includes(search) ||
      cert.verification_code?.toLowerCase().includes(search)
    );
  });

  const getRankBadge = (rank, type) => {
    if (type === 'winner' || rank <= 3) {
      if (rank === 1) return { bg: 'bg-amber-500/20', text: 'text-amber-400', label: '1st' };
      if (rank === 2) return { bg: 'bg-gray-500/20', text: 'text-gray-300', label: '2nd' };
      if (rank === 3) return { bg: 'bg-orange-500/20', text: 'text-orange-400', label: '3rd' };
    }
    return { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: `#${rank}` };
  };

  return (
    <div className="space-y-6">
      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Award className="text-amber-400" size={20} />
              </div>
              <span className="text-gray-400 text-sm">Total Certificates</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.total_certificates}</p>
            <p className="text-xs text-gray-500 mt-1">
              {analytics.winner_certificates} winners | {analytics.participant_certificates} participants
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <FileDown className="text-cyan-400" size={20} />
              </div>
              <span className="text-gray-400 text-sm">Total Downloads</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.analytics?.total_downloads || 0}</p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Eye className="text-emerald-400" size={20} />
              </div>
              <span className="text-gray-400 text-sm">Verifications</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.analytics?.total_verifications || 0}</p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <TrendingUp className="text-blue-400" size={20} />
              </div>
              <span className="text-gray-400 text-sm">LinkedIn Shares</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics.analytics?.total_linkedin_shares || 0}</p>
          </div>
        </div>
      )}

      {/* Certificates by Contest */}
      {analytics?.certificates_by_contest?.length > 0 && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <BarChart3 size={18} className="text-purple-400" />
            Certificates by Contest
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {analytics.certificates_by_contest.slice(0, 8).map((item, idx) => (
              <div key={idx} className="bg-white/5 rounded-lg p-3">
                <p className="text-gray-400 text-xs truncate">{item.contest_title || 'Unknown Contest'}</p>
                <p className="text-white font-bold">{item.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search by name, email, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              data-testid="cert-search-input"
            />
          </div>
        </div>
        
        <select
          value={selectedContest}
          onChange={(e) => { setSelectedContest(e.target.value); setPage(1); }}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          data-testid="contest-filter"
        >
          <option value="">All Contests</option>
          {contests.map(c => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        
        <select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          data-testid="type-filter"
        >
          <option value="">All Types</option>
          <option value="winner">Winners Only</option>
          <option value="participant">Participants Only</option>
        </select>
        
        <button
          onClick={bulkGenerateCertificates}
          disabled={!selectedContest || bulkGenerating}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors disabled:opacity-50"
          data-testid="bulk-generate-btn"
        >
          {bulkGenerating ? <Loader size={16} className="animate-spin" /> : <Award size={16} />}
          Bulk Generate
        </button>
        
        <button
          onClick={() => { fetchCertificates(); fetchAnalytics(); }}
          className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={18} className="text-gray-400" />
        </button>
      </div>

      {/* Certificates Table */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5 border-b border-white/10">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Contest</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Rank</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Score</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Issued</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <Loader className="animate-spin text-cyan-400 mx-auto" size={32} />
                  </td>
                </tr>
              ) : filteredCerts.length > 0 ? (
                filteredCerts.map((cert) => {
                  const badge = getRankBadge(cert.rank, cert.certificate_type);
                  return (
                    <tr key={cert.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{cert.student_name || 'N/A'}</p>
                        <p className="text-gray-500 text-xs">{cert.student_email || ''}</p>
                        <p className="text-gray-600 text-xs">{cert.college || ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-300 text-sm">{cert.contest_title}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-lg text-sm font-bold ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-emerald-400 font-semibold">{cert.score}</span>
                        <span className="text-gray-500 text-xs">/{cert.total_participants}</span>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded">
                          {cert.verification_code}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-400 text-sm">
                          {new Date(cert.issued_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <a
                            href={`/codearena/certificate/${cert.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 bg-cyan-500/20 rounded-lg hover:bg-cyan-500/30 transition-colors"
                            title="View Certificate"
                          >
                            <Eye size={14} className="text-cyan-400" />
                          </a>
                          <button
                            onClick={() => deleteCertificate(cert.id)}
                            className="p-1.5 bg-rose-500/20 rounded-lg hover:bg-rose-500/30 transition-colors"
                            title="Delete Certificate"
                          >
                            <Trash2 size={14} className="text-rose-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    No certificates found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
            <span className="text-gray-400 text-sm">
              Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <ChevronLeft size={18} className="text-gray-400" />
              </button>
              <span className="px-3 py-2 text-white">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeArenaCertificatesAdmin;
